import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { reduxForm, formValueSelector } from 'redux-form';
import { FormattedMessage } from 'react-intl';
import messages from '../../text/messages';
import Tabs from './../../components/tabs';
import RejectICW2Form from '../../components/forms/rejectICW2';
import Modal from './../../components/modal';
import GeneralTab from './tabs/general';
import BackgroundCheckTab from './tabs/backgroundCheck';
import DocumentsTab from './tabs/documents';
import ClassificationsTab from './tabs/classifications';
import AdminReviewTab from './tabs/adminReview';
import WizardButtons from '../../components/wizard/navigator/buttons';
import { Snackbar } from '../../components/snackbar';
import { history } from '../../configuration/store';
import { getPosition } from '../../reducers/positions';
import { getApproval, approveJob, resetJobOnboarding, approveIC } from './../../reducers/approvals';
import { getJob } from '../../reducers/jobs';
import { getFullName } from '../../utilities/string';
import { setProgress } from '../../reducers/wizard';
import { getJobProjects } from '../../reducers/timekeeping';

import './approval.css';
import { jobStatus } from '../../utilities/jobStatus';

class ApprovalPage extends React.Component {
    constructor(props) {
        super(props);

        this.approve = this.approve.bind(this);
        this.getApproveType = this.getApproveType.bind(this);
        this.classification = this.classification.bind(this);

        // modal controls
        this.state = { modal: false };
        this.onOpen = this.onOpen.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onReject = this.onReject.bind(this);
    }

    componentDidMount() {
        const { match, getApproval, getPosition, getJobProjects } = this.props;
        if (match && match.params) {
            getApproval(match.params.id, result => {
                getPosition(result.position_id);
                getJobProjects(result.id);
            });
        }
    }

    approve() {
        const { approval, approveJob, approveIC, setProgress } = this.props;
        const postAction = () => {
            Snackbar.success(<FormattedMessage id={'jobApproved'} defaultMessage={'Job successfully approved.'} />);
            history.push('/approvals');
        };

        if (approval.progress && approval.progress.branch === 'ic-continue-request') {
            approveIC(approval, () => {
                setProgress({ entity: 'job', id: approval.id }, { ...approval.progress, step: -1, branch: 'ic-continue' }, postAction);
            });
        } else {
            approveJob(approval, postAction);
        }
    }

    onOpen() {
        const { job } = this.props;
        this.setState({ modal: true, job: { ...job } });
    }

    onClose() {
        this.setState({ modal: false, job: undefined });
    }

    onReject() {
        const { approval, getJob, resetJobOnboarding } = this.props;

        const newClassification = this.classification() === 'w2' ? 'ic' : 'w2';

        getJob(approval, job => {
            resetJobOnboarding(job, newClassification);
            Snackbar.success(<FormattedMessage id={'jobRejected'} defaultMessage={'Worker onboarding reset.'} />);
            history.push('/approvals');
        });
    }

    getApproveType() {
        const {
            approval: { classify_result, classify_client_pref, classify_worker_pref }
        } = this.props;

        const desiredResults = ['w2', 'ic'];

        if (desiredResults.includes(classify_result)) {
            return { result: classify_result, reason: <FormattedMessage id={'classify.basedOnResult'} defaultMessage={'Based on classify result.'} /> };
        }

        if (classify_client_pref === 'w2') {
            return {
                result: 'w2',
                reason: <FormattedMessage id={'classify.basedOnClient'} defaultMessage={'Client specified W2 only.'} />
            };
        }

        if (classify_client_pref === 'ic-only') {
            return {
                result: 'ic',
                reason: <FormattedMessage id={'classify.basedOnClient'} defaultMessage={'Client specified IC only.'} />
            };
        }

        if (classify_client_pref === 'ic-only') {
            return {
                result: 'ic',
                reason: <FormattedMessage id={'classify.basedOnClient'} defaultMessage={'Based on client classification preference.'} />
            };
        }

        if (desiredResults.includes(classify_worker_pref)) {
            return {
                result: classify_worker_pref,
                reason: <FormattedMessage id={'classify.basedOnWorker'} defaultMessage={'Based on worker classification preference.'} />
            };
        }

        return {
            reason: <FormattedMessage id={'classify.lackOfInfo'} defaultMessage={'Classification selection missing.'} />
        };
    }

    classification() {
        const { approval } = this.props;

        switch (jobStatus(approval)) {
            case 'w2':
                return 'w2';

            case 'ic':
            case 'ic-continue-request':
            default:
                return 'ic';
        }
    }

    render() {
        const { approval, position, handleSubmit, submitting, projects } = this.props;
        if (!approval || !position || !projects) {
            return null;
        }
        const { modal } = this.state;

        const approvalType = this.getApproveType();
        let tabs = [
            {
                title: <FormattedMessage {...messages.general} />,
                icon: 'format_align_justify',
                content: <GeneralTab job={approval} position={position} approvalType={approvalType} projects={projects} />
            },
            { title: <FormattedMessage {...messages.documents} />, icon: 'library_books', content: <DocumentsTab job={approval} position={position} /> }
        ];

        const questionnaireTab = {
            title: <FormattedMessage {...messages.questionnaire} />,
            icon: 'playlist_add_check',
            content: <ClassificationsTab job={approval} />
        };

        switch (this.classification()) {
            case 'w2':
                tabs = [
                    ...tabs,
                    { title: <FormattedMessage {...messages.backgroundCheck} />, icon: 'thumbs_up_down', content: <BackgroundCheckTab job={approval} /> },
                    questionnaireTab
                ];
                break;

            case 'ic':
            default:
                tabs = [...tabs, questionnaireTab];
                break;
        }

        tabs = [...tabs, { title: <FormattedMessage {...messages.notes} />, icon: 'notes', content: <AdminReviewTab job={approval} /> }];

        const title = (
            <FormattedMessage
                id={'jobApplication'}
                defaultMessage={"{name}'s {position} application"}
                values={{ name: getFullName(approval.user), position: approval.title }}
            />
        );

        return (
            <div className="page">
                <div className="row section">
                    <div className="col-xs-12">
                        <Tabs items={tabs} pageWide title={title} isPageTitle={true} />
                    </div>
                </div>

                <form onSubmit={handleSubmit(this.approve)}>
                    <WizardButtons
                        disabled={submitting || !approvalType.result}
                        reject={{ action: this.onOpen }}
                        cancel={{ action: () => history.push('/approvals') }}
                        submit={{ message: <FormattedMessage {...messages.approveType} values={{ type: approvalType.result }} /> }}
                    />
                </form>
                <Modal
                    open={modal}
                    onClose={this.onClose}
                    managedClose
                    size="col-sm-8 col-sm-offset-2 col-xs-12 extend-modal"
                    title={<FormattedMessage {...messages.rejectClassification} />}
                >
                    <RejectICW2Form job={approval} icw2={this.classification()} onSubmit={this.onReject} cancel={this.onClose} />
                </Modal>
            </div>
        );
    }
}

ApprovalPage.meta = {
    hide: true,
    locations: ['/approval/']
};

ApprovalPage = reduxForm({
    form: 'approval-form',
    destroyOnUnmount: false,
    keepDirtyOnReinitialize: true,
    enableReinitialize: true,
    forceUnregisterOnUnmount: false
})(ApprovalPage);

const mapStateToProps = state => {
    const selector = formValueSelector('approval-form');
    let approval = state.approvals.approval;
    let position = approval ? state.positions[approval.position_id] : undefined;
    return {
        role: state.identity.role,
        projects: state.timekeeping.projects,
        approval,
        position,
        classify_result: selector(state, 'classify_result'),
        initialValues: { classify_result: approval ? approval.classify_result : undefined }
    };
};

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            getApproval,
            approveJob,
            approveIC,
            resetJobOnboarding,
            setProgress,
            getJob,
            getPosition,
            getJobProjects
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(ApprovalPage);



// WEBPACK FOOTER //
// ./src/pages/approval/index.jsx